import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { reverseUrl } from '../../../../../navigation/reverseUrl'
import { societyQueries } from '../../../../../api/societies'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { ticketQueries } from '../../../../../api/ticket'
import { PillButton } from '../../../../../components/ticket/PillButton'
import { TicketListView } from '../TicketListView/TicketListView'
import { notificationsQueries } from '../../../../../api/notifications'
import { useCurrentSociety } from '../../../../../hooks/useCurrentSociety'
import { useAuth } from '../../../../../hooks/useAuth'
import { Icon, IconChoices } from '../../../../../components/common/Icon/Icon'
import { DropdownSelect } from '../../../../../components/common/DropdownSelect/DropdownSelect'
import { SocietyAvatar } from '../../../../../components/common/Avatar/Avatar'
import { DropdownItemContent } from '../../../../../components/feed/DropdownItemContent/DropdownItemContent'
import { theme } from '../../../../../theme/theme'

export const SocietyResidentTicketSideBarContent = (): React.ReactNode => {
  const navigate = useNavigate()
  const { society } = useCurrentSociety()
  const { userId } = useAuth()
  const { data: userSocieties } = useQuery(
    societyQueries.getUserSocieties(userId)
  )
  const { translate } = useAppTranslation()

  const [variant, setVariant] = useState<'open' | 'all'>('open')

  const { data: notifications } = useQuery(
    notificationsQueries.getUserUnseenUnreadCount(userId)
  )

  const { data: openUnreadCount } = useQuery(
    ticketQueries.getUnreadTicketsCount(
      { customerIds: userId, status: ['open'] },
      society?._id
    )
  )

  return (
    <div className="flex min-h-0 flex-1 flex-col px-6 pt-6">
      <h2 style={theme.textVariants.h2} className="mb-4">
        {translate('ticketView.title')}
      </h2>
      <div className="flex w-full justify-between items-center gap-2">
        <Link
          className="flex gap-2"
          to={reverseUrl('society:home', { id: society?._id })}
        >
          <Icon icon={IconChoices.ARROW_LEFT} />
        </Link>

        {userSocieties?.length && userSocieties.length > 1 && (
          <DropdownSelect
            value={society?._id}
            options={
              userSocieties?.map((_society) => ({
                value: _society._id,
                label: _society.name,
                image: <SocietyAvatar society={_society} size={32} />,
                displayNotificationBadge:
                  !!notifications?.tickets[_society._id],
              })) || []
            }
            renderItemContent={(option) => (
              <DropdownItemContent option={option} />
            )}
            onChange={(value) =>
              navigate(reverseUrl('society:tickets', { id: value }))
            }
            dropdownWidth="w-full"
          />
        )}
        <Link
          className=""
          to={reverseUrl('society:ticket-create', { id: society?._id })}
        >
          <Icon icon={IconChoices.NEW_MESSAGE} />
        </Link>
      </div>

      <div className="flex gap-2 mt-4">
        <PillButton
          title={translate('ticketNav.open')}
          onClick={() => setVariant('open')}
          active={variant === 'open'}
          notificationCount={openUnreadCount}
        />
        <PillButton
          title={translate('ticketNav.all')}
          onClick={() => setVariant('all')}
          active={variant === 'all'}
        />
      </div>
      <TicketListView variant={variant} />
    </div>
  )
}
